import { generateTextFromTipTapContent, tiptapContentSchema } from '../util';
import {
  type ProjectMember,
  type ProjectComment,
  ProjectContentType,
  ProjectType,
  type EternalChallengeProject,
} from '../lib/projects';
import { z } from 'zod';
import {
  type HackathonProject,
  Track,
  type HackathonWithPhase,
  SortDirection,
} from '../lib';

export const retrieveProjectSchema = z.object({
  type: z.nativeEnum(ProjectType).default(ProjectType.HACKATHON).optional(),
  slug: z.string().optional(),
  includeSurvey: z.coerce.boolean().default(false).optional(),
});

export type RetrieveProjectQuery = z.infer<typeof retrieveProjectSchema>;

export type HackathonProjectResponse = {
  projectType: typeof ProjectType.HACKATHON;
  project: HackathonProject<ProjectMember>;
  projectCompletion: {
    isComplete: boolean;
    fieldErrors: string[];
  };
};

export type EternalProjectResponse = {
  projectType: typeof ProjectType.ETERNAL;
  project: EternalChallengeProject;
  projectCompletion: {
    isComplete: boolean;
    fieldErrors: string[];
  };
};

export type RetrieveProjectResponse<T = ProjectType> =
  T extends typeof ProjectType.HACKATHON
    ? HackathonProjectResponse
    : EternalProjectResponse;

export const ProjectSortOptions = {
  RANDOM: 'RANDOM',
  NAME: 'NAME',
  LIKES: 'LIKES',
  COMMENTS: 'COMMENTS',
} as const;
export type ProjectSortOptions =
  (typeof ProjectSortOptions)[keyof typeof ProjectSortOptions];

export const ProjectListMode = {
  HACKATHON_ENDED: 'HACKATHON_ENDED',
  WINNERS_ANNOUNCED: 'WINNERS_ANNOUNCED',
} as const;
export type ProjectListMode =
  (typeof ProjectListMode)[keyof typeof ProjectListMode];

const baseFilterSchema = z.object({
  hackathonId: z.coerce.number(),
  nameQuery: z.string().optional(),
  tracks: z.array(z.nativeEnum(Track)).optional(),
  countries: z.array(z.string()).optional(),
  showWinnersOnly: z.coerce.boolean().optional().default(false),
  limit: z.coerce.number().optional().default(18),
  offset: z.coerce.number().optional().default(0),
  seed: z
    .string()
    .length(16)
    .regex(/^[a-f0-9]{16}$/, { message: 'Invalid random seed.' })
    .optional(),
});

const reviewPhaseFilteringSchema = baseFilterSchema.extend({
  sort: z.literal(ProjectSortOptions.RANDOM),
});

const postHackathonFilteringSchema = baseFilterSchema.extend({
  sort: z.enum([
    ProjectSortOptions.NAME,
    ProjectSortOptions.LIKES,
    ProjectSortOptions.COMMENTS,
  ]),
  direction: z.nativeEnum(SortDirection).default(SortDirection.DESC).optional(),
});

export const listProjectsSchema = z.discriminatedUnion('sort', [
  reviewPhaseFilteringSchema,
  postHackathonFilteringSchema,
]);

export type ListProjectsQuery = z.infer<typeof listProjectsSchema>;

export interface ListProjectsResponse {
  projects: HackathonProject<ProjectMember>[];
  hackathon: HackathonWithPhase;
  offset: number;
  seed: string | null;
  hasMore: boolean;
}

export type DeleteProjectResponse = {
  message: string;
  projectId: number;
};

export const createProjectCommentSchema = z.object({
  body: tiptapContentSchema.refine(
    (data) => {
      try {
        const text = generateTextFromTipTapContent(data);
        return text.length > 0;
      } catch {
        return false;
      }
    },
    {
      message: 'Comment body is required',
      path: [],
    },
  ),
  projectId: z.number(),
  parentCommentId: z.number().optional(),
});

export type CreateProjectCommentBody = z.infer<
  typeof createProjectCommentSchema
>;

export type CreateProjectCommentResponse = {
  message: string;
};

export const editProjectCommentSchema = z.object({
  commentId: z.number(),
  body: tiptapContentSchema.refine(
    (data) => {
      try {
        const text = generateTextFromTipTapContent(data);
        return text.length > 0;
      } catch {
        return false;
      }
    },
    {
      message: 'Comment body is required',
      path: [],
    },
  ),
});

export type EditProjectCommentBody = z.infer<typeof editProjectCommentSchema>;

export type EditProjectCommentResponse = {
  message: string;
};

export const listProjectCommentsSchema = z.object({
  projectId: z.coerce.number(),
  parentCommentId: z.coerce.number().optional(),
  limit: z.coerce.number().default(20).optional(),
  offset: z.coerce.number().default(0).optional(),
});

export type ListProjectCommentsQuery = z.infer<
  typeof listProjectCommentsSchema
>;

export interface ListProjectCommentsResponse {
  comments: ProjectComment[];
  hasMore: boolean;
  offset: number;
}

export const deleteProjectCommentSchema = z.object({
  commentId: z.number(),
});

export type DeleteProjectCommentBody = z.infer<
  typeof deleteProjectCommentSchema
>;

export interface DeleteProjectCommentResponse {
  message: string;
}

export const createProjectLikeSchema = z.object({
  likedContentType: z.nativeEnum(ProjectContentType),
  contentId: z.number(),
});

export type CreateProjectLikeBody = z.infer<typeof createProjectLikeSchema>;

export interface CreateProjectLikeResponse {
  message: string;
}

export const deleteProjectLikeSchema = z.object({
  likableContentType: z.nativeEnum(ProjectContentType),
  contentId: z.number(),
});

export type DeleteProjectLikeBody = z.infer<typeof deleteProjectLikeSchema>;

export interface DeleteProjectLikeResponse {
  message: string;
}

export const retrieveProjectCommentSchema = z.object({
  commentId: z.coerce.number(),
});

export type RetrieveProjectCommentQuery = z.infer<
  typeof retrieveProjectCommentSchema
>;

export interface RetrieveProjectCommentResponse {
  comment: ProjectComment;
}

export const retrieveProjectLikeSchema = z.object({
  contentId: z.coerce.number(),
  contentType: z.nativeEnum(ProjectContentType),
});

export type RetrieveProjectLikeQuery = z.infer<
  typeof retrieveProjectLikeSchema
>;

export interface RetrieveProjectLikeResponse {
  likeCount: number;
  likedByCurrentUser: boolean;
}

export const exportProjectsSchema = z.object({
  hackathonId: z.coerce.number(),
});

export type ExportProjectsQuery = z.infer<typeof exportProjectsSchema>;
